import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { PIC, SVG_MUI } from "@ryde/assets";
import { ExpandedBlock } from "@ryde/controls";
import { usePassengersMapBulkActions, useSelectedIds } from "@ryde/redux";
import {
  IPassengersMapAssignUsers,
  IPassengersMapStationInfo,
  IWarkDistanceAndTime,
  onActivateKeyDown,
  PLURAL_KEY,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";

import {
  ExpandInactiveCollapseIconStyled,
  PassengersMapStationInfoPopupStyled,
} from "./passengers-map-station-popup.styled";

interface IMapStationInfoPopupProps {
  stationInfo: IPassengersMapStationInfo;
  assignPassengers: IPassengersMapAssignUsers[];
  distanceAndTimeCacheRefByIds: Map<string, IWarkDistanceAndTime>;
}

export const PassengersMapStationPopup: FC<IMapStationInfoPopupProps> = ({
  distanceAndTimeCacheRefByIds,
  assignPassengers,
  stationInfo,
}) => {
  const [isInactiveExpanded, setInactiveExpanded] = useState<boolean>(false);
  const { t } = useTranslation();
  const { openEditFormDrawer } = useDrawers();
  const { bulkRefetchPassengersStationMapData } = usePassengersMapBulkActions();
  const { selectedRowsIds } = useSelectedIds("passenger");

  const { hasPermissions: updateStationPermission } = useHasPermissions({
    entity: "station",
    action: "update",
  });
  const { hasPermissions: updatePassengerPermission } = useHasPermissions({
    entity: "passenger",
    action: "update",
  });

  const tableSelectedPassengersIds = useMemo(() => {
    return new Set(selectedRowsIds);
  }, [selectedRowsIds]);

  const onEditStation = useEvent(async (id: number): Promise<void> => {
    if (updateStationPermission && id) {
      const res = await openEditFormDrawer({
        entity: "station",
        id,
      });

      if (res.success && res.data?.id) {
        bulkRefetchPassengersStationMapData();
      }
    }
  });

  const onEditPassenger = useEvent(async (id: number): Promise<void> => {
    if (updatePassengerPermission && id) {
      const res = await openEditFormDrawer({
        entity: "passenger",
        id,
      });

      if (res.success && res.data?.id) {
        bulkRefetchPassengersStationMapData();
      }
    }
  });

  const getDurationPathInfo = useCallback(
    (passenger: IPassengersMapAssignUsers) => {
      const isHomeAddress = stationInfo.isHomeAddress;
      const { sourceStation1Id, sourceStation2Id } = passenger;

      if (sourceStation1Id && sourceStation2Id) {
        const originStationId = isHomeAddress
          ? sourceStation1Id
          : sourceStation2Id;
        const destStationId = isHomeAddress
          ? sourceStation2Id
          : sourceStation1Id;

        const durationPathInfo = distanceAndTimeCacheRefByIds.get(
          `${originStationId},${destStationId}`,
        );

        if (durationPathInfo) {
          return (
            <>
              <span>
                {durationPathInfo.distance} {t("general.metersShort")}
              </span>
              <span>
                {durationPathInfo?.duration.minutes}:
                {durationPathInfo?.duration.seconds} {t("general.minuteShort")}
              </span>
            </>
          );
        }
      }

      return null;
    },
    [distanceAndTimeCacheRefByIds, stationInfo.isHomeAddress, t],
  );

  const passengersRows = useCallback(
    (passengers: IPassengersMapAssignUsers[]) => {
      return (
        <>
          {passengers?.map((passenger, idx) => (
            <div className="passenger-row" key={`${passenger.id}_${idx}`}>
              <SVG_MUI.User2SVG
                className="passenger-icon"
                onClick={() => onEditPassenger(passenger.id)}
              />

              <div className="passenger-info">
                <div
                  className={clsx("first-line", {
                    selected: tableSelectedPassengersIds.has(passenger?.id),
                  })}
                >
                  <span className="passenger-name">
                    {passenger.firstName} {passenger.lastName}
                  </span>
                  <span className="passenger-id">{passenger.id}</span>
                </div>

                <div className="second-line">
                  {getDurationPathInfo(passenger)}
                </div>
              </div>
            </div>
          ))}
        </>
      );
    },
    [onEditPassenger, getDurationPathInfo, tableSelectedPassengersIds],
  );

  const { activePassengers, inactivePassengers } = useMemo(() => {
    const active: IPassengersMapAssignUsers[] = [];
    const inactive: IPassengersMapAssignUsers[] = [];

    for (const passenger of assignPassengers) {
      const target = passenger.isActive ? active : inactive;
      const isSelected = tableSelectedPassengersIds.has(passenger.id);

      if (isSelected) {
        target.unshift(passenger);
      } else {
        target.push(passenger);
      }
    }

    return {
      activePassengers: active,
      inactivePassengers: inactive,
    };
  }, [assignPassengers, tableSelectedPassengersIds]);

  return (
    <PassengersMapStationInfoPopupStyled>
      <div className="station-info-header" title={stationInfo.fullAddress}>
        {stationInfo.addressName} ({stationInfo.id})
      </div>

      <div className="content">
        {passengersRows(activePassengers)}

        {activePassengers?.length === 0 && (
          <div className="empty-content">. . .</div>
        )}

        {Boolean(inactivePassengers?.length) && (
          <div
            className={clsx("inactive-btn-container", {
              expend: isInactiveExpanded,
            })}
            onClick={() => setInactiveExpanded(prev => !prev)}
            onKeyDown={onActivateKeyDown(() =>
              setInactiveExpanded(prev => !prev),
            )}
            role="button"
            tabIndex={0}
          >
            <span>
              {t("general.inactive", { entity: t(`passenger.${PLURAL_KEY}`) })}
            </span>
            <ExpandInactiveCollapseIconStyled
              $isExpanded={isInactiveExpanded}
            />
          </div>
        )}

        <AnimatePresence initial={false}>
          {isInactiveExpanded && (
            <ExpandedBlock blockHeight={"fit-content"}>
              {passengersRows(inactivePassengers)}
            </ExpandedBlock>
          )}
        </AnimatePresence>
      </div>

      <div className="footer">
        <div
          className="edit-button"
          onClick={() => onEditStation(stationInfo.id)}
          onKeyDown={onActivateKeyDown(() => onEditStation(stationInfo.id))}
          role="button"
          tabIndex={0}
        >
          <PIC.PencilSVG className="pencil-icon" />
          <span>{t("general.EDIT")}</span>
        </div>
      </div>
    </PassengersMapStationInfoPopupStyled>
  );
};
